import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import Box from '@mui/material/Box';
import SpecificationData from './specificationData';
import FlexBox from '@/atomic-components/FlexBox';
import { getStyle } from '@/utils/theme/fonts';
import { getColor } from '@/utils/theme/colors';
import Specstile from './measurementUnitSelectionSwitch';
import { LencoImage } from '@/atomic-components/Image';
import RightArrowWithColor from '../../../../assets/pdp/rightArrowIconWithMintyColor.svg';
import SpecificationAndDimensionDesignDrawerComponent from './specificationDrawer';
import { Inclusion } from '@/services/catalogue/models/pdp';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role='tabpanel'
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					<LencoTypography>{children}</LencoTypography>
				</Box>
			)}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

interface IBasicTabsProps {
	inclusions: Inclusion[];
	productType: string;
}

export default function SpecificationTab(props: IBasicTabsProps) {
	const [value, setValue] = React.useState(0);
	const [specificationUnit, setSpecificationUnit] = React.useState('cm');
	const [openSpecificationDrawer, setOpenSpecificationDrawer] =
		React.useState(false);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const InclusionProductLabel = props.inclusions.map((d) => d.label);

	return (
		<Box sx={{ width: '100%' }}>
			<FlexBox>
				<LencoTypography
					sx={{
						...getStyle('H9_Medium'),
						color: getColor('Gray 0'),
						paddingRight: '16px',
					}}
				>
					Specifications
				</LencoTypography>
				<Specstile
					setSpecificationUnit={setSpecificationUnit}
					specificationUnit={specificationUnit}
				/>
			</FlexBox>

			{props.productType != 'ITEM' && (
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<Tabs
						value={value}
						onChange={handleChange}
						aria-label='basic tabs example'
					>
						{InclusionProductLabel.map((label, index) => {
							return (
								<Tab
									label={label}
									{...a11yProps(index)}
									key={label}
									style={{ textTransform: 'capitalize', paddingLeft: '0' }}
								/>
							);
						})}
					</Tabs>
				</Box>
			)}
			{props.inclusions.map((inclusionData, index) => {
				const { data } = inclusionData.specifications;
				return (
					<TabPanel value={value} index={index} key={index}>
						<FlexBox gap={2} sx={{ alignItems: 'start' }}>
							<FlexBox direction='column' sx={{ width: '55%' }}>
								{data.map((specs, index) => {
									return index < 2 ? (
										<Box sx={{ width: '100%' }}>
											<FlexBox
												direction='column'
												key={specs.type}
												sx={{ paddingBottom: '16px', paddingTop: '16px' }}
											>
												<LencoTypography
													sx={{
														...getStyle('H14_SemiBold'),
														color: getColor('Gray -1'),
													}}
												>
													{specs.type}
												</LencoTypography>
												{specs.data.map((specsInfo, index) => {
													return (
														<FlexBox key={specsInfo.key}>
															<LencoTypography
																sx={{
																	...getStyle('P_Regular'),
																	color: getColor('Gray -1'),
																	whiteSpace: 'nowrap',
																}}
															>
																{specsInfo.key}:
															</LencoTypography>
															<LencoTypography
																sx={{
																	...getStyle('P_Medium'),
																	color: getColor('Gray -1'),
																	paddingLeft: '8px',
																}}
															>
																{typeof specsInfo.value == 'string'
																	? specsInfo?.value
																	: specsInfo?.value?.filter((x: any) => {
																			return x.unit == specificationUnit;
																	  })[0]?.value}
															</LencoTypography>
														</FlexBox>
													);
												})}
											</FlexBox>

											{index < 1 && index < data.length - 1 && (
												<Box
													sx={{
														width: '100%',
														borderTop: `1px dashed ${getColor('Gray -3')}`,
													}}
												></Box>
											)}
										</Box>
									) : (
										''
									);
								})}
								{data.length > 2 && (
									<LencoTypography
										sx={{
											color: getColor('Minty +1'),
											...getStyle('P_Small'),
											paddingTop: '16px',
											cursor: 'pointer',
											marginBottom: '46px',
											textDecoration: 'underline',
											whiteSpace: 'nowrap',
											'&:hover': {
												...getStyle('P_Medium'),
												transition: '0.3s all',
											},
										}}
										onClick={() => {
											setOpenSpecificationDrawer(true);
										}}
									>
										See all Specifications{' '}
										<Box
											component='span'
											sx={{
												paddingTop: '2px',
												paddingLeft: '4px',
											}}
										>
											<RightArrowWithColor />
										</Box>
									</LencoTypography>
								)}
								<SpecificationAndDimensionDesignDrawerComponent
									specification={inclusionData?.specifications}
									dimensionImages={inclusionData?.dimensionImages}
									setOpenSpecificationDrawer={setOpenSpecificationDrawer}
									openSpecificationDrawer={openSpecificationDrawer}
								/>
							</FlexBox>
							<Box sx={{ width: '45%', paddingLeft: '24px' }}>
								{inclusionData?.dimensionImages &&
									inclusionData?.dimensionImages?.length > 0 && (
										<Box
											sx={{
												width: '100%',
											}}
											key={inclusionData?.dimensionImages[0].url}
										>
											<LencoImage
												url={inclusionData.dimensionImages[0].url}
												aspectRatio={
													inclusionData.dimensionImages[0].aspectRatio
												}
											/>
										</Box>
									)}
							</Box>
						</FlexBox>
					</TabPanel>
				);
			})}
		</Box>
	);
}
